import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, jsonBody, queryString } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { formatDate } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Notice,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";
import { SkeletonTable } from "../../ui/skeletons";

interface OwnershipTransfer {
  id: string;
  tenantId: string;
  tenantName: string;
  fromName: string;
  fromEmail: string;
  toName: string;
  toEmail: string;
  status: string;
  reason: string;
  initiatedAt: string;
  acceptedAt?: string;
  expiresAt: string;
  version: number;
}

type Resolution = { transfer: OwnershipTransfer; action: "verify" | "reject" };
interface SensitiveState {
  unlocked: boolean;
  markProtectedError(error: unknown): boolean;
}

export function OwnershipQueue({
  canManage,
  sensitive,
}: {
  canManage: boolean;
  sensitive: SensitiveState;
}) {
  const transfers = useQuery({
    queryKey: ["operator", "ownership-transfers"],
    queryFn: () =>
      api<ListResponse<OwnershipTransfer>>(
        `/api/v1/operator/ownership-transfers${queryString({ status: "pending_verification" })}`,
      ),
  });
  const [resolution, setResolution] = useState<Resolution>();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function resolve(reason: string) {
    if (!resolution) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(
        `/api/v1/operator/ownership-transfers/${resolution.transfer.id}/${resolution.action}`,
        { method: "POST", body: jsonBody({ reason, version: resolution.transfer.version }) },
      );
      setResolution(undefined);
      await transfers.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (transfers.isLoading) return <SkeletonTable columns={7} rows={4} />;
  if (transfers.error) return <ErrorPanel error={transfers.error} />;
  const items = transfers.data?.items ?? [];
  return (
    <Card>
      <CardHeader
        title="Ownership verification queue"
        description="Accepted tenant ownership changes remain inert until a Pixel Apocalypse owner verifies the request and records the evidence reviewed."
      />
      {error ? <ErrorPanel error={error} /> : null}
      {!canManage ? (
        <Notice tone="info">
          Only the Pixel Apocalypse platform owner can complete or reject ownership transfers.
        </Notice>
      ) : null}
      {items.length ? (
        <DataTable
          caption="Ownership transfer verification queue"
          items={items}
          getKey={(item) => item.id}
          columns={[
            {
              label: "Tenant",
              render: (item) => (
                <span>
                  <strong>{item.tenantName}</strong>
                  <br />
                  <small className="mono">{item.tenantId}</small>
                </span>
              ),
            },
            {
              label: "Current owner",
              render: (item) => (
                <span>
                  {item.fromName}
                  <br />
                  <small>{item.fromEmail}</small>
                </span>
              ),
            },
            {
              label: "Incoming owner",
              render: (item) => (
                <span>
                  {item.toName}
                  <br />
                  <small>{item.toEmail}</small>
                </span>
              ),
            },
            { label: "Accepted", render: (item) => formatDate(item.acceptedAt) },
            { label: "Expires", render: (item) => formatDate(item.expiresAt) },
            { label: "Status", render: (item) => <StatusPill value={item.status} /> },
            {
              label: "Decision",
              render: (item) =>
                canManage ? (
                  <span className="inline">
                    <button
                      className="button primary small"
                      type="button"
                      disabled={working || !sensitive.unlocked}
                      onClick={() => setResolution({ transfer: item, action: "verify" })}
                    >
                      Verify
                    </button>
                    <button
                      className="button danger small"
                      type="button"
                      disabled={working || !sensitive.unlocked}
                      onClick={() => setResolution({ transfer: item, action: "reject" })}
                    >
                      Reject
                    </button>
                  </span>
                ) : (
                  "Awaiting platform owner"
                ),
            },
          ]}
        />
      ) : (
        <EmptyState
          title="No ownership transfers awaiting verification"
          description="Accepted requests will appear here until verified, rejected, canceled, or expired."
        />
      )}
      <ReasonDialog
        open={Boolean(resolution)}
        onClose={() => setResolution(undefined)}
        onSubmit={resolve}
        title={
          resolution?.action === "verify"
            ? "Verify ownership transfer"
            : "Reject ownership transfer"
        }
        description={
          resolution
            ? `${resolution.transfer.toName} will ${resolution.action === "verify" ? "become tenant owner and both affected sessions will be revoked" : "remain in their existing role"}.`
            : undefined
        }
        initialReason={
          resolution?.action === "verify"
            ? "Identity, authority, and customer authorization were independently verified."
            : "Verification requirements were not satisfied."
        }
        submitLabel={resolution?.action === "verify" ? "Verify and transfer" : "Reject transfer"}
        tone={resolution?.action === "verify" ? "primary" : "danger"}
        minLength={10}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
    </Card>
  );
}
